<script lang="ts">
	import { getPriceForProductOfSize } from "../../../../core/schema/utils/getPriceForProductOfSize.js";
	import { isProductForCustomerConfigurable } from "../../../../core/schema/utils/isProductForCustomerConfigurable.js";
	import type { OrderProductOptions } from "../../../../core/schema/OrderProductOptions.js";
	import { defaultSize } from "../../../../core/schema/defaultSize.js";
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import { cartStore } from "../../stores/cart/cartStore.js";
	import { onMount } from "svelte";
	import type { ProductSize } from "../../../../core/schema/ProductSize.js";
	import { doesProductForCustomerHaveMultipleSizes } from "../../../../core/schema/utils/doesProductForCustomerHaveMultipleSizes.js";
	import { cartPathMap } from "../../../../core/schema/paths/cartPathMap.js";
	import type { ProductForCustomer } from "../../../../core/schema/Product.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import Button from "../Button.svelte";
	import cake from "../../../../core/assets/icons/cake.svg?raw";
	import { formatPrice } from "../../../../core/schema/utils/formatPrice.js";
	import { portionsPerSize } from "../../../../core/schema/portionsPerSize.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import { reportAddedProductToCart, reportProductDetail } from "../../UniversalAnalytics.js";
	import { getNearestDeliveryDateForProduct } from "../../../../core/schema/utils/getNearestDeliveryDate.js";
	import { createEmptyOptions } from "../../../../core/schema/utils/createEmptyOptions.js";
	import { getProductSizes } from "../../../../core/schema/utils/getProductSizes.js";
	import { toDayLong } from "../../../../core/utils/toDayLong.js";
	import { toTimeFormat } from "../../../../core/utils/toTimeFormat.js";
	import { toDateMonthFormat } from "../../../../core/utils/toDateMonthFormat.js";
	import Form from "../Form/Form.svelte";
	import Link from "../Link.svelte";

	export let product: ProductForCustomer;

	const { TENANT_LOCALE } = getGlobalSettings();

	const options = createEmptyOptions(defaultSize);
	const putItemToCart = createPromiseStore(
		async (product: ProductForCustomer, quantity: number, options?: OrderProductOptions) => {
			const response = await cartStore.addProduct(product, quantity, product.minimumOrderQuantity, options);
			reportAddedProductToCart(response.item, TENANT_LOCALE);
			return response;
		},
	);

	let configurationDrawerOpen = false;

	let selectedSize: ProductSize = defaultSize;
	let alreadyMounted = false;
	onMount(() => {
		const searchParams = new URLSearchParams(window.location.search);
		const size = searchParams.get("size") as ProductSize | null;
		if (size !== null) {
			selectedSize = size;
			options.size = size;
		}
		if (searchParams.has("config")) {
			configurationDrawerOpen = true;
		}
		alreadyMounted = true;

		reportProductDetail(product, TENANT_LOCALE, selectedSize);
	});
	$: {
		if (doesProductForCustomerHaveMultipleSizes(product) && alreadyMounted) {
			const searchParams = new URLSearchParams(window.location.search);
			const size = searchParams.get("size");
			if (size !== selectedSize) {
				searchParams.set("size", selectedSize);
				window.history.replaceState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
			}
		}
	}

	async function submit(): Promise<void> {
		await putItemToCart.call(
			product,
			1,
			isProductForCustomerConfigurable(product, false) || doesProductForCustomerHaveMultipleSizes(product)
				? options
				: undefined,
		);
		window.location.assign(`/${cartPathMap[TENANT_LOCALE]}`);
	}

	async function saveConfiguratorOptions({ detail }: CustomEvent<OrderProductOptions>): Promise<void> {
		await putItemToCart.call(product, 1, detail);
		window.location.assign(`/${cartPathMap[TENANT_LOCALE]}`);
	}

	function selectSize(size: string): void {
		selectedSize = size as ProductSize;
		reportProductDetail(product, TENANT_LOCALE, selectedSize);
	}

	const defaultPrice = getPriceForProductOfSize(product);

	const dayInflectMap: Record<string, string> = {
		neděle: "neděli",
		pondělí: "pondělí",
		úterý: "úterý",
		středa: "středu",
		čtvrtek: "čtvrtek",
		pátek: "pátek",
		sobota: "sobotu",
	};

	const heightClasses = ["h-[1.5rem]", "h-[1.625rem]", "h-[1.75rem]", "h-[2rem]"];
	const widthClasses = ["w-[1.5rem]", "w-[1.625rem]", "w-[1.75rem]", "w-[2rem]"];

	$: sizes = getProductSizes(product.basicPrice);
	$: nearestDeliveryDate = getNearestDeliveryDateForProduct(product);
	$: dayLong = toDayLong(nearestDeliveryDate, TENANT_LOCALE);

	// eslint-disable-next-line no-warning-comments
	// FIXME remove this after upgrade to Svelte 5.
	/* eslint-disable @typescript-eslint/explicit-function-return-type */

	const loadProductConfigurator = createPromiseStore(() => import("../Configurator/ProductConfigurator.svelte"));

	$: {
		if (configurationDrawerOpen && $loadProductConfigurator.status === "initial") {
			void loadProductConfigurator.call();
		}
	}
</script>

{#if $loadProductConfigurator.status === "success"}
	{@const ProductConfigurator = $loadProductConfigurator.result.default}
	<ProductConfigurator
		bind:isOpen={configurationDrawerOpen}
		{product}
		{options}
		on:saveOptions={saveConfiguratorOptions}
	/>
{/if}

<Form on:submit={submit} class="mt-8">
	{#if sizes.length > 1}
		<span class="mb-3 font-semibold leading-7">Vyberte si velikost</span>
		<div class="flex flex-col gap-2">
			{#each sizes as size, i}
				{@const selected = options.size === size}
				<label
					for="size-{size}"
					class="flex w-full cursor-pointer justify-between rounded-lg border py-[0.875rem] pl-3 pr-6 {selected
						? 'outline-secondaryBlack border-transparent outline outline-2'
						: ''}"
				>
					<span class="relative flex items-center gap-3">
						<input
							bind:group={options.size}
							on:click={() => {
								selectSize(size);
							}}
							class="custom-radio"
							id="size-{size}"
							name="size"
							type="radio"
							value={size}
							required
						/>
						<label for="size-{size}" class="{heightClasses[i]} {widthClasses[i]} uppercase">{size}</label>
						<span class="leading-6 {options.size === size ? 'text-primaryRed font-semibold' : ''}">
							{portionsPerSize[size]} porcí
						</span>
					</span>
					<span class="text-md font-semibold leading-7 {options.size === size ? 'text-primaryRed' : ''}">
						{nbspify(formatPrice(getPriceForProductOfSize(product, size)?.withTax ?? 0, TENANT_LOCALE, false))}
					</span>
				</label>
			{/each}
		</div>
	{:else}
		<div class="flex flex-col items-end gap-4 lg:justify-end">
			<span class="text-right text-xl font-semibold leading-7">
				{nbspify(formatPrice(defaultPrice?.withTax ?? 0, TENANT_LOCALE, false))}
			</span>
			<div class="text-secondaryBlack flex items-end gap-2 text-[.8rem] leading-[1] opacity-60">
				bez DPH
				<span class="text-right text-base font-semibold">
					{nbspify(formatPrice(defaultPrice?.withoutTax ?? 0, TENANT_LOCALE, false))}
				</span>
			</div>
		</div>
	{/if}
	<div class="my-8">
		<div
			class="group relative mb-2 w-fit cursor-pointer pr-4 font-semibold text-black after:pointer-events-none after:absolute after:-left-12 after:bottom-full after:h-4 after:w-full after:min-w-[18rem] after:content-[''] hover:after:pointer-events-auto sm:text-base"
		>
			K vyzvednutí nejdříve
			<span
				class="bg-grey-200 absolute right-0 top-0 ml-0.5 grid aspect-square h-3 place-items-center rounded-full text-[9px] font-bold"
			>
				?
			</span>

			<div
				role="none"
				class="border-1 border-secondaryBlack-250 bg-secondaryBlack-100 after:border-t-secondaryBlack-100 before:border-t-secondaryBlack-250 absolute bottom-full left-0 z-50 mb-3 hidden min-w-[15rem] cursor-default rounded-lg px-5 pb-[1.375rem] pt-4 text-xs before:absolute before:left-3 before:top-full before:border-l-[calc(.45rem+1.5px)] before:border-r-[calc(.45rem+1.5px)] before:border-t-[calc(.75rem+1.5px)] before:border-transparent before:content-[''] after:absolute after:left-[calc(.75rem+1px)] after:top-full after:border-l-[.45rem] after:border-r-[.45rem] after:border-t-[.75rem] after:border-transparent after:content-[''] group-hover:flex sm:mb-[.875rem] sm:min-w-[18rem] sm:before:border-l-[calc(.45rem+1px)] sm:before:border-r-[calc(.45rem+1px)] sm:before:border-t-[calc(.75rem+1px)]"
				on:click={(event) => {
					if (!(event.target instanceof HTMLAnchorElement)) {
						event.preventDefault();
					}
				}}
			>
				<div class="!font-normal">
					Potřebujete dort na poslední chvíli a systém Vám neumožní objednat třeba na zítra? Zavolejte prosím na jednu z
					našich poboček
					<Link class="font-semibold" href="tel:00420775033520">Čakovice</Link>
					nebo <Link class="font-semibold" href="tel:00420775033520">Petřiny</Link> a kolegové s Vámi zpracují 'Last call'
					objednávku.
				</div>
			</div>
		</div>
		<p>
			{dayLong === dayInflectMap["středa"] || dayLong === dayInflectMap["čtvrtek"] ? "Ve" : "V"}
			<span class="font-semibold">
				{dayInflectMap[dayLong]}
				{toDateMonthFormat(nearestDeliveryDate, TENANT_LOCALE)} od {toTimeFormat(nearestDeliveryDate, TENANT_LOCALE)}
			</span> na pobočce, kterou si vyberete v košíku.
		</p>
	</div>
	<div class="mt-6 flex flex-col gap-2 sm:flex-row-reverse md:flex-col xl:flex-row-reverse">
		{#if product.minimumOrderQuantity > 1}
			<p class="text-secondaryBlack text-sm font-semibold">
				Minimální objednávka: {product.minimumOrderQuantity} ks
			</p>
		{/if}
		<Button
			type="submit"
			class="w-full justify-center !rounded-[1.875rem] py-[1.063rem] text-[1.125rem] leading-6 md:text-[1.375rem]"
			variant="primary"
			icon={cake}
			iconClass="w-6"
			disabled={$putItemToCart.status === "loading"}
		>
			Přidat do krabice
		</Button>
		{#if isProductForCustomerConfigurable(product, false)}
			<Button
				class="w-full justify-center !rounded-[1.875rem] py-[1.063rem] text-[1.125rem] leading-6 md:text-[1.375rem]"
				type="button"
				variant="secondary"
				on:click={() => {
					options.size = selectedSize;
					configurationDrawerOpen = true;
				}}
				disabled={$loadProductConfigurator.status === "loading"}
			>
				{#if $loadProductConfigurator.status === "loading"}
					Načítám…
				{:else}
					Upravit
				{/if}
			</Button>
		{/if}
	</div>
</Form>
